import React, { useEffect, useRef, useState } from 'react';
import * as uuid from 'uuid';
import { Elements, OnLoadParams } from 'react-flow-renderer';
import { IconButton } from '@mui/material';
import { Create } from '@mui/icons-material';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
	closeDrawer,
	Drawer,
	getSubscriptions,
	LoadingPage,
	NavigationPrompt,
	openDrawer,
	Text,
	WarningDialog,
	useIsFeatureEnabled,
} from '@innovyze/stylovyze';
import { RehabAction } from '@Types';
import {
	runRehabTree,
	setRefreshList,
	clearScanId,
	refreshCostTablesAndRun,
	setSaveRun,
} from '@Actions';
import {
	selectCurrentTree,
	selectIsLoading,
	edgeConnector,
	selectRehabActions,
	selectTreeValidationResult,
	startNodeIdSelector,
	selectAddNewActionToTree,
	selectGetRehabTreeStatus,
	selectGetRehabTreeValidationStatus,
	selectRehabDeletedActions,
	selectUpdateActionInTree,
	selectSaveRun,
} from '@Selectors';
import { useDispatch } from 'react-redux';
import { FullPageWrapper } from '@Utils/styles';
import { QueryDrawer, RenameRehab } from '@Components';
import './dnd.css';
import RehabActionList from '@Components/RehabActionList';
import { RehabActionDrawer } from '@Components/RehabActionDrawer';
import { RehabActionDrawerV2 } from '@Components/RehabActionDrawerV2';
import { CostTableStalenessWarning } from '@Components/CostTableStalenessWarning';
import RehabResult from '@Components/RehabResult';
import { fullAccess, viewerAccess } from '@innovyze/shared-utils';
import {
	resolveNodeForEdit,
	checkActionExists,
	getQueryFormValues,
} from './decisionTreeHelpers';
import { useGlobalization } from '@Translations';
import { useTreeStatusPolling } from './useTreeStatusPolling';
import { useTreeSave } from './useTreeSave';
import { useTreeSync } from './useTreeSync';
import { useTreeElements } from './useTreeElements';
import { TreeDashboardTab } from './TreeDashboardTab';
import { useDetailPanel } from './useDetailPanel';

export const DecisionTree = () => {
	const { t } = useGlobalization();
	const subscriptions = getSubscriptions();
	const hasFullAccess = fullAccess(subscriptions);
	const hasViewerAccess = viewerAccess(subscriptions);
	const shouldEnableMultipleInputs = useIsFeatureEnabled(
		'info-360-asset-rehab-multiple-inputs',
	);
	const isCostTablesEnabled = !!useIsFeatureEnabled(
		'info360-asset-cost-tables-rehab',
	);
	const isTrenchCalculationEnabled = !!useIsFeatureEnabled(
		'i360-rehab-defect-based-costs',
	);
	const history = useHistory();
	const location = useLocation<{ name?: string }>();
	const dispatch = useDispatch();
	const params: { treeId: string } = useParams();
	const [currentTreeName, setCurrentTreeName] = useState(
		location.state?.name ?? '',
	);
	const [isDirty, setIsDirty] = useState(false);
	const edgeConnectorObject = edgeConnector();
	const [reactFlowInstance, setReactFlowInstance] =
		useState<OnLoadParams | null>(null);
	const [elements, setElements] = useState<Elements>([]);
	const [nodeIdToEdit, setNodeIdToEdit] = useState<undefined | string>(
		undefined,
	);
	const isLoading = selectIsLoading();
	const [rehabActionToEdit, setRehabActionToEdit] = useState<
		undefined | null | RehabAction
	>(undefined);
	const [editName, setEditName] = useState(false);
	const [loadResults, setLoadResults] = useState<boolean>(true);

	const [currentTab, setCurrentTab] = useState<number>(0);

	const [addActionDialogOpen, setAddActionDialogOpen] = useState(false);
	const [addActionSelection, setAddActionSelection] = useState('');
	const [defectCodes, setDefectCodes] = useState<string[] | null>(null);
	const currentTree = selectCurrentTree();

	const {
		assetDetailActions,
		showDetailInspection,
		showDetailAsset,
		detInsp,
		detAss,
	} = useDetailPanel({
		hasFullAccess,
		currentTreeSystemType: currentTree?.systemType,
	});
	const rehabTreeStatus = selectGetRehabTreeStatus();
	const rehabTreeValidationStatus = selectGetRehabTreeValidationStatus();
	const rehabTreeValidationResult = selectTreeValidationResult();
	const currentTreeStartNodeId = startNodeIdSelector();
	const [startNodeConnectedLimit, setStartNodeConnectedLimit] =
		useState(false);

	const addNewActionToTree = selectAddNewActionToTree();
	const updateActionInTree = selectUpdateActionInTree();

	const rehabActions = selectRehabActions();
	const rehabDeletedActions = selectRehabDeletedActions();

	const {
		isRehabTreeRunning,
		isRehabTreeValidating,
		isRehabTreeEditable,
		isDisabled,
	} = useTreeStatusPolling({
		treeId: params.treeId,
		rehabTreeStatus,
		rehabTreeValidationStatus,
		hasFullAccess,
		hasViewerAccess,
		isLoading,
	});

	const handleNodeEditClick = (nodeId: string) => {
		setAddActionSelection('');
		setNodeIdToEdit('');

		setNodeIdToEdit(nodeId);

		const resolved = resolveNodeForEdit(
			nodeId,
			currentTree.nodes,
			elements,
			reactFlowInstance?.getElements(),
		);

		if (resolved) {
			if (resolved.type === 'query') {
				dispatch(openDrawer('addQueryDrawer'));
			} else {
				setAddActionSelection(resolved.actionId ?? '');
				setAddActionDialogOpen(true);
			}
		} else {
			// Fallback: search within the setElements callback
			setElements(ele => {
				const fallback = resolveNodeForEdit(
					nodeId,
					undefined,
					ele,
					undefined,
				);
				if (fallback) {
					if (fallback.type === 'query') {
						dispatch(openDrawer('addQueryDrawer'));
					} else {
						setAddActionSelection(fallback.actionId ?? '');
						setAddActionDialogOpen(true);
					}
				}
				return ele;
			});
		}
	};

	const {
		addToTree,
		updateInTree,
		onConnect,
		handleElementsRemove,
		handleNodeDragStop,
		nodeHandlers,
	} = useTreeElements({
		reactFlowInstance,
		elements,
		setElements,
		setIsDirty,
		currentTreeStartNodeId,
		shouldEnableMultipleInputs,
		onEditClick: handleNodeEditClick,
		isRehabTreeEditable,
		setStartNodeConnectedLimit,
		setNodeIdToEdit,
	});

	useTreeSync({
		treeId: params.treeId,
		currentTree,
		edgeConnectorObject,
		rehabDeletedActions,
		shouldEnableMultipleInputs,
		elements,
		setElements,
		setIsDirty,
		setCurrentTreeName,
		nodeHandlers,
		isRehabTreeEditable,
		addToTree,
		updateInTree,
		addNewActionToTree,
		updateActionInTree,
		defectCodes,
	});

	const { handleSaveTree } = useTreeSave({
		reactFlowInstance,
		elements,
		currentTree,
		rehabActions,
		rehabDeletedActions,
		shouldEnableMultipleInputs,
		setIsDirty,
	});

	const saveRun = selectSaveRun();
	const handleSaveTreeRef = useRef(handleSaveTree);
	handleSaveTreeRef.current = handleSaveTree;

	useEffect(() => {
		if (saveRun) {
			dispatch(setSaveRun(false));
			handleSaveTreeRef.current();
		}
	}, [saveRun]);

	const [queryFormKey, setQueryFormKey] = useState(uuid.v4());

	const addQueryHandler = () => {
		setNodeIdToEdit('');
		setQueryFormKey(uuid.v4());
		dispatch(openDrawer('addQueryDrawer'));
	};

	const handleDrawerClose = () => {
		setNodeIdToEdit('');
		dispatch(closeDrawer());
	};

	const validateRehabTreeHandler = (treeId: string) => {
		if (
			rehabTreeValidationResult.treeId === treeId &&
			!rehabTreeValidationResult.isValid
		) {
			dispatch(openDrawer('validationMessagesDrawer'));
		}
	};

	const runRehabTreeHandler = (treeId: string) => {
		if (
			rehabTreeValidationResult.treeId === treeId &&
			rehabTreeValidationResult.isValid
		) {
			dispatch(clearScanId());
			dispatch(runRehabTree({ treeId: treeId }));
		}
	};

	const rehabResultTab = () => {
		return (
			<RehabResult
				viewerAccess={hasViewerAccess}
				treeId={params.treeId}
				assetAction={assetDetailActions}
				systemType={currentTree.systemType}
				assetType={currentTree.assetType}
				loadResults={loadResults}
				onResultsLoaded={setLoadResults}
			/>
		);
	};

	const checkActionExistsInTree = (action: RehabAction) =>
		checkActionExists(action, elements);

	const rehabActionTab = () => {
		return (
			<RehabActionList
				viewerAccess={hasViewerAccess}
				isLoading={isLoading}
				onRehabActionChange={setRehabActionToEdit}
				checkActionExists={checkActionExistsInTree}
				isDirty={isDirty}
				isSaveDisabled={isDisabled}
				isRehabTreeValidating={isRehabTreeValidating}
				handleSaveTree={handleSaveTree}
				trenchCalculationEnabled={isTrenchCalculationEnabled}
			/>
		);
	};

	const secondaryNav = () => {
		const tabs = [
			{
				content: (
					<>
						{isCostTablesEnabled && rehabActions?.length > 0 && (
							<CostTableStalenessWarning
								actions={rehabActions}
								isDisabled={isDisabled || isLoading}
								onUpdateAndRun={updates =>
									dispatch(
										refreshCostTablesAndRun({
											treeId: params.treeId,
											updates,
										}),
									)
								}
							/>
						)}
						<TreeDashboardTab
							elements={elements}
							isDirty={isDirty}
							isDisabled={isDisabled}
							isRehabTreeEditable={isRehabTreeEditable}
							isRehabTreeRunning={isRehabTreeRunning}
							isRehabTreeValidating={isRehabTreeValidating}
							hasFullAccess={hasFullAccess}
							rehabActions={rehabActions}
							rehabTreeValidationResult={
								rehabTreeValidationResult
							}
							nodeIdToEdit={nodeIdToEdit}
							setNodeIdToEdit={setNodeIdToEdit}
							defectCodes={defectCodes}
							setDefectCodes={setDefectCodes}
							addActionDialogOpen={addActionDialogOpen}
							setAddActionDialogOpen={setAddActionDialogOpen}
							addActionSelection={addActionSelection}
							setAddActionSelection={setAddActionSelection}
							reactFlowInstance={reactFlowInstance}
							setReactFlowInstance={setReactFlowInstance}
							onConnect={onConnect}
							handleElementsRemove={handleElementsRemove}
							handleNodeDragStop={handleNodeDragStop}
							addToTree={addToTree}
							setRehabActionToEdit={setRehabActionToEdit}
							handleSaveTree={handleSaveTree}
							treeId={params.treeId}
							currentTree={currentTree}
							runRehabTreeHandler={runRehabTreeHandler}
							validateRehabTreeHandler={validateRehabTreeHandler}
							addQueryHandler={addQueryHandler}
						/>
					</>
				),
				title: t('Configuration'),
			},
			{ content: rehabResultTab(), title: t('Results') },
			{
				content: rehabActionTab(),
				title: t('Actions'),
			},
		];
		return tabs;
	};

	const itemIndexChanged = (itemIndex: number) => {
		setCurrentTab(itemIndex);
	};

	const onEditName = () => {
		setEditName(true);
	};

	const onEditNameClose = (saved: boolean, newName: string) => {
		setEditName(false);
		if (saved) {
			dispatch(setRefreshList(true));
			setCurrentTreeName(newName);
		}
	};

	const shouldAddNewActionToTree = () => {
		// Only add new actions to the tree when it's showing
		return currentTab === 0;
	};

	if (!currentTreeName && !currentTree.name) {
		return <LoadingPage />;
	}

	return (
		<>
			<RenameRehab
				open={editName}
				treeId={currentTree._id}
				name={currentTreeName}
				onClose={onEditNameClose}
			/>
			<NavigationPrompt
				when={isDirty && hasFullAccess}
				navigate={nextLocation => {
					history.push(nextLocation);
				}}
				confirmText={t('Discard')}
				shouldBlockNavigation={() => true}
				shouldBlockReload={isDirty}
				content={t(
					'You have modified your tree configuration and have unsaved changes. You can discard changes, or cancel to continue editing.',
				)}></NavigationPrompt>
			{isCostTablesEnabled ? (
				<RehabActionDrawerV2
					treeId={params.treeId}
					viewerAccess={hasViewerAccess}
					rehabAction={rehabActionToEdit}
					shouldAddNewActionToTree={shouldAddNewActionToTree}
					setIsDirty={setIsDirty}
					trenchCalculationEnabled={isTrenchCalculationEnabled}
				/>
			) : (
				<RehabActionDrawer
					treeId={params.treeId}
					viewerAccess={hasViewerAccess}
					rehabAction={rehabActionToEdit}
					shouldAddNewActionToTree={shouldAddNewActionToTree}
					setIsDirty={setIsDirty}
				/>
			)}
			{detInsp}
			{detAss}
			{!showDetailInspection() && !showDetailAsset() ? (
				<FullPageWrapper
					className={isLoading ? 'wait' : ''}
					isLoading={isLoading && !currentTree.name}
					title={currentTreeName || currentTree.name || ''}
					minorHeader={
						!hasViewerAccess && currentTreeName.length != 0 ? (
							<div
								style={{
									paddingLeft: '20px',
									paddingRight: '20px',
								}}>
								<IconButton
									title={t('Edit Name')}
									data-cy="editButton"
									onClick={onEditName}>
									<Create />
								</IconButton>
							</div>
						) : (
							<></>
						)
					}
					applyPadding={true}
					headerUnderline
					secondaryNav={{
						items: secondaryNav(),
						itemIndex: currentTab,
						itemIndexChanged: itemIndexChanged,
					}}
					breadcrumbs={[
						{ title: t('Analysis'), link: '/analysis' },
						{ title: t('Rehab Decision Trees'), link: '/rehab/' },
					]}>
					{currentTree.name ? (
						<QueryDrawer
							key={queryFormKey}
							isNew={
								nodeIdToEdit == undefined || nodeIdToEdit == ''
									? true
									: false
							}
							queryValues={getQueryFormValues(
								elements,
								nodeIdToEdit,
							)}
							allNodes={elements}
							onAddToTree={addToTree}
							currentTree={currentTree}
							onDrawerClose={handleDrawerClose}></QueryDrawer>
					) : (
						<></>
					)}
					<Drawer
						drawerId="validationMessagesDrawer"
						onCancel={() => dispatch(closeDrawer())}
						title={t('Validation Errors')}>
						{rehabTreeValidationResult?.validationMessages?.map(
							(message, index) => {
								return (
									!message.errorMessage.includes(
										'_clone_',
									) && (
										<div key={index}>
											<Text variant={'heading-medium'}>
												{t('ERROR: {{message}}', {
													message:
														message.errorMessage,
												})}
											</Text>
											<br></br>
										</div>
									)
								);
							},
						)}
					</Drawer>
				</FullPageWrapper>
			) : (
				[]
			)}
			<WarningDialog
				open={startNodeConnectedLimit}
				title={t('The Main Tree Connection')}
				content={t(
					'You cannot have two main trees. You should connect the start node with only one node.',
				)}
				cancelText={t('Cancel')}
				//confirmText={'confirm'}
				onCancel={() => setStartNodeConnectedLimit(false)}
				onConfirm={() => setStartNodeConnectedLimit(false)}
			/>
		</>
	);
};

export default DecisionTree;
